/* eslint-disable react/jsx-sort-props */
/* eslint-disable @typescript-eslint/naming-convention */
import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { Table } from "@dealer-center/core-components";
import type { IncentivesRecord } from "@dealer-center/ddc-pages-incentives-manager-interface";
import { Locale } from "@dealer-center/ddc-core-utils";
import {
  capitalizeString,
  incentiveDate,
  onlyUniqueEntries
} from "../utils/utils";
import "./IncentivesTable.scss";

const PAGE_SIZE = 100;

let intervalId;

interface IncentivesTableColumn {
  className: string;
  dataIndex: string;
  title: string | React.ReactElement;
  render?: (value: any) => string | React.ReactElement;
}

interface IncentivesTableData {
  id: number;
  key: string;
  ACTION_NEEDED: string | React.ReactElement;
  SOURCE: string;
  CONDITION: string | React.ReactElement;
  YEARS: number[];
  MAKE: string;
  MODELS: string[];
  OFFER: string;
  TYPE: string | React.ReactElement;
  CONDITIONAL_OFFER: string;
  FEATURED: string;
  EFFECTIVE_DATE: string;
  EXPIRATION_DATE: string | React.ReactElement;
  TOTAL_CASH_BACK: string;
}

const StyledTableContents = styled.div`
  position: relative;
  height: 100%;
  overflow: hidden;
`;

const StyledNoTableData = styled.div`
  padding-top: 20px;
  text-align: center;
`;

const StyledPastDate = styled.span`
  color: orange;
  font-weight: bold;
`;

const isDateInPast = (timestamp: number): boolean => {
  const today = new Date();
  const dateToCheck = new Date(incentiveDate(timestamp));

  today.setHours(0, 0, 0, 0);
  dateToCheck.setHours(0, 0, 0, 0);

  return dateToCheck.getTime() < today.getTime();
};

const onChangeSelectedRows = (
  selectedRows: string[],
  records: IncentivesTableData[],
  setSelectedKeys: (keys: string[]) => void,
  setSelectedIndices: (indices: number[]) => void
) => {
  const selectedIndices = records
    .map((record: IncentivesTableData) => record.id)
    .sort((a, b) => a - b);
  setSelectedIndices(selectedIndices);
  setSelectedKeys(selectedRows);
};

const onRowClick = (
  record: IncentivesTableData,
  event: any,
  setSelectedKeys: (keys: string[]) => void,
  setSelectedIndices: (indices: number[]) => void
) => {
  if (!event.target?.className?.includes("checkbox-input")) {
    setSelectedIndices([record.id]);
    setSelectedKeys([record.key]);
  }
};

const onRowHover = (record, event) => {
  const classMatch = (value: string | undefined, match: string) => {
    if (typeof value === "string") {
      return value.includes(match);
    }
    return false;
  };

  const el = event.target;
  const className = event.target?.className;
  let values: string[] = [];
  let max = 2;
  if (classMatch(className, "column-MODELS")) {
    values = record.MODELS;
    max = 1;
  } else if (classMatch(className, "column-YEARS")) {
    values = record.YEARS;
  } else if (classMatch(className, "column-VIN")) {
    values = record.VIN;
  }
  if (values.length) {
    if (values.length > max) {
      el.setAttribute("title", values.join(", "));
    } else {
      el.removeAttribute("title");
    }
  }
};

const boolString = (value: boolean) => {
  return value
    ? (Locale.getText("YES") as string)
    : (Locale.getText("NO") as string);
};

const moneyString = (value: number) => {
  if (value < 0) {
    return "";
  }
  return Locale.getPrice(value);
};

const percentString = (value: number) => {
  if (value < 0) {
    return "";
  }
  return `${value}%`;
};

const positiveNumber = (value: number) => {
  if (value < 0) {
    return "";
  }
  return value;
};

interface Dimension {
  width: number;
  height: number;
}

interface SortableDetails {
  sortableColumns?: string[];
  sorter?: Record<string, (a: any, b: any) => number>;
}

export const IncentivesTable = ({
  columns,
  data,
  pageSize = PAGE_SIZE,
  onSelections,
  onRowDoubleClick
}: {
  columns: string[];
  data: IncentivesRecord[];
  pageSize?: number;
  onSelections?: (selectedIndices: number[]) => void;
  onRowDoubleClick?: (record: IncentivesRecord) => void;
}) => {
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [cols, setCols] = useState<IncentivesTableColumn[]>([]);
  const [rows, setRows] = useState<IncentivesTableData[]>([]);
  const [sortableDetails, setSortableDetails] = useState<SortableDetails>({});
  const [tableDimensions, setTableDimensions] = useState<Dimension>({
    width: 800,
    height: 500
  });
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [selectedIndices, setSelectedIndices] = useState<number[]>([]);

  useEffect(() => {
    if (columns && data) {
      const sortableColumns: string[] = [];
      const c: IncentivesTableColumn[] = [];
      const sorter = {};
      columns.forEach((column: string) => {
        const cinfo: IncentivesTableColumn = {
          className: `column-${column}`,
          dataIndex: column,
          title: capitalizeString(Locale.getText(column) as string)
        };
        if (column === "YEARS" || column === "VIN") {
          cinfo.render = (values: number[]) => {
            if (values.length > 2) {
              return `${values[0]} ${Locale.getText("AND_N_MORE", { n: values.length - 1 })}`;
            }
            return values.join(", ");
          };
        } else if (column === "MODELS") {
          cinfo.render = (values: string[]) => {
            if (values.length > 1) {
              return `${values.length} ${(Locale.getText("MODELS") as string).toLowerCase()}`;
            }
            return values[0];
          };
        }
        c.push(cinfo);

        if (column !== "MODELS") {
          sortableColumns.push(column);
          if (column === "YEARS") {
            sorter["YEARS"] = (a, b) => {
              return a.YEARS[0] - b.YEARS[0];
            };
          } else if (column === "VIN") {
            sorter["VIN"] = (a, b) => {
              const avin: string = a.VIN.length ? a.VIN[0] : "";
              const bvin: string = b.VIN.length ? b.VIN[0] : "";
              return avin > bvin ? 1 : avin < bvin ? -1 : 0;
            };
          } else if (
            ["TOTAL_CASH_BACK", "MSRP", "CASH_DUE", "RESIDUAL_VALUE"].includes(
              column
            )
          ) {
            sorter[column] = (a: string, b: string) => {
              const isEnglish = a[column][0] === "$";
              let valueA = a[column].replace("$", "");
              let valueB = b[column].replace("$", "");
              if (isEnglish) {
                valueA = Number(valueA.replace(/,/g, ""));
                valueB = Number(valueB.replace(/,/g, ""));
              } else {
                valueA = Number(valueA.replace(/,/g, ".").replace(/\s/g, ""));
                valueB = Number(valueB.replace(/,/g, ".").replace(/\s/g, ""));
              }
              return valueA - valueB;
            };
          } else if (["EFFECTIVE_DATE", "EXPIRATION_DATE"].includes(column)) {
            sorter[column] = (a: any, b: any) => {
              const getDateString = (value: any) => {
                if (typeof value === "string") {
                  return value;
                }

                if (
                  value &&
                  typeof value === "object" &&
                  value.props &&
                  value.props.children
                ) {
                  return value.props.children;
                }
                return "";
              };

              const dateStringA = getDateString(a[column]);
              const dateStringB = getDateString(b[column]);

              const isUSA = dateStringA.includes("/");
              if (isUSA) {
                const dateA = new Date(dateStringA);
                const dateB = new Date(dateStringB);
                return dateA.getTime() - dateB.getTime();
              }
              return dateStringA > dateStringB
                ? 1
                : dateStringA < dateStringB
                  ? -1
                  : 0;
            };
          } else if (column === "CUSTOM_LENGTH") {
            sorter[column] = (a: number | string, b: number | string) => {
              const valA = Number(a[column]);
              const valB = Number(b[column]);
              return valA - valB;
            };
          }
        }
      });

      const r: IncentivesTableData[] = data.map(
        (item: IncentivesRecord, index: number) => ({
          id: index,
          key: item.content.incentiveId || index.toString(),
          ACTION_NEEDED: Locale.getText(
            item.accountMap?.status || "ACTION_NEEDED"
          ),
          SOURCE: item.creatorAccountName,
          CONDITION: Locale.getText(item.applicableCondition),
          YEARS: onlyUniqueEntries(
            item.applicableVehicleList.map(v => v.year).sort()
          ),
          MAKE: item.applicableMake,
          MODELS: item.applicableVehicleList.map(
            v => `${v.year} ${v.model} ${v.bodyStyle}`
          ),
          OFFER: item.content.title || "",
          TYPE: Locale.getText(item.type) || "",
          CONDITIONAL_OFFER: boolString(item.conditional),
          FEATURED: boolString(item.featured),
          EFFECTIVE_DATE:
            Locale.getDateFormat(
              incentiveDate(item.effectiveDate),
              Locale.getCurrent()
            ) || "",
          EXPIRATION_DATE: (() => {
            const formattedDate =
              Locale.getDateFormat(
                incentiveDate(item.expirationDate),
                Locale.getCurrent()
              ) || "";

            if (isDateInPast(item.expirationDate)) {
              return <StyledPastDate>{formattedDate}</StyledPastDate>;
            }

            return formattedDate;
          })(),
          TOTAL_CASH_BACK: moneyString(item.cashOption),
          MO24: percentString(item.twentyFourMonthApr),
          MO36: percentString(item.thirtySixMonthApr),
          MO39: percentString(item.thirtyNineMonthApr),
          MO42: percentString(item.fortyTwoMonthApr),
          MO48: percentString(item.fortyEightMonthApr),
          MO60: percentString(item.sixtyMonthApr),
          MO72: percentString(item.seventyTwoMonthApr),
          CUSTOM_LENGTH: positiveNumber(item.customTermMonths || -1),
          CUSTOM_RATE: percentString(item.customApr || -1),
          MSRP: moneyString(item.msrp || -1),
          CASH_DUE: moneyString(item.cashDue || -1),
          RESIDUAL_VALUE: moneyString(item.purchaseOptionPrice || -1),
          VIN: item.vinList,
          SHORT_TITLE: item.extractedShortTitle || ""
        })
      );
      setSortableDetails({ sortableColumns, sorter });
      setCols(c);
      setRows(r);
      setSelectedKeys([]);
      setSelectedIndices([]);
    }
  }, [columns, data]);

  useEffect(() => {
    const adjustTableDimensions = () => {
      if (tableContainerRef.current) {
        const width = tableContainerRef.current.clientWidth;
        const height = tableContainerRef.current.clientHeight;
        if (
          width !== tableDimensions.width ||
          height !== tableDimensions.height
        ) {
          setTableDimensions({ width, height });
        }
      }
    };
    if (intervalId) {
      clearInterval(intervalId);
    } else {
      adjustTableDimensions();
    }
    intervalId = setInterval(() => {
      adjustTableDimensions();
    }, 500);
    return () => {
      clearInterval(intervalId);
    };
  }, [tableDimensions.width, tableDimensions.height]);

  useEffect(() => {
    if (onSelections) {
      onSelections(selectedIndices);
    }
  }, [onSelections, selectedIndices]);

  const isPaginating = rows.length > pageSize;
  const scrollYoffset = isPaginating ? 110 : 50;

  return (
    <StyledTableContents className="incentivesTable" ref={tableContainerRef}>
      {rows?.length === 0 && (
        <StyledNoTableData>{Locale.getText("EMPTY_TABLE")}</StyledNoTableData>
      )}
      {rows?.length > 0 && (
        <Table.Table
          key={rows.length.toString()}
          displayHeader
          enableRowSelection
          enablePagination={isPaginating}
          id="TableTest"
          scrollX={tableDimensions.width - 100}
          scrollY={tableDimensions.height - scrollYoffset}
          selectedRowKeys={selectedKeys}
          data={rows}
          defaultPageSize={pageSize}
          displayFilter={false}
          sortableDetails={sortableDetails}
          columns={cols}
          tableLayout="auto"
          onChangeSelectedRows={(selectedRows, records) =>
            onChangeSelectedRows(
              selectedRows as string[],
              records,
              setSelectedKeys,
              setSelectedIndices
            )
          }
          onRowCallback={(record, index) => ({
            onClick: event => {
              onRowClick(record, event, setSelectedKeys, setSelectedIndices);
            },
            onDoubleClick: () => {
              if (onRowDoubleClick && data[record.id]) {
                onRowDoubleClick(data[record.id]);
              }
            },
            onMouseEnter: event => {
              onRowHover(record, event);
            },
            onMouseMove: event => {
              onRowHover(record, event);
            },
            onMouseLeave: event => {
              onRowHover(record, event);
            }
          })}
        />
      )}
    </StyledTableContents>
  );
};
